/**
 * Schoonmaak, onderhoud en inspectie: wat er tussen twee gasten moet gebeuren.
 *
 * Dit scherm heeft één vraag te beantwoorden: wat moet er vandaag en de komende
 * dagen gebeuren, en is het al gedaan? Alles wat daar niet aan bijdraagt, staat
 * er niet.
 *
 * Drie keuzes die daaruit volgen:
 *
 * 1. **Achterstallig staat apart en bovenaan.** Een taak van gisteren die nog open
 *    staat, is een ander soort bericht dan een taak van volgende week. Ze door
 *    elkaar tonen op datum maakt van de eerste een regel die je voorbijscrolt.
 * 2. **Afvinken is één klik en direct.** Wie schoonmaakt, staat met een telefoon in
 *    de hand in een woning. Een bevestigingsdialoog is daar geen zorgvuldigheid
 *    maar een obstakel — en terugzetten kan gewoon.
 * 3. **Afgeronde taken verdwijnen niet.** Ze zakken naar onderen. "Is dit al
 *    gedaan?" is de vraag die elke dag terugkomt bij twee mensen op dezelfde
 *    lijst, en een verdwenen taak beantwoordt hem niet.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { useFormatters } from '@siteshowroom/i18n/react'
import type { Formatters } from '@siteshowroom/i18n'

import { useTranslate } from '../lib/i18n-provider'
import { listResidences } from '../lib/media-client'
import type { ResidenceOption } from '../lib/media-client'
import {
  TASK_KINDS,
  createTask,
  deleteTask,
  listTasks,
  taskErrorMessageKey,
  today,
  updateTask,
} from '../lib/tasks-client'
import type { Task, TaskFailure, TaskKind } from '../lib/tasks-client'

/**
 * Een taakdatum is een kalenderdatum in de tijdzone van de woning; hij wordt in
 * UTC gelezen én getoond, zoals overal (§17.6).
 */
function formatCalendarDate(formatters: Formatters, value: string): string {
  return formatters.formatDate(`${value}T00:00:00Z`, { timeZone: 'UTC' })
}

function FailureNotice({ error }: { readonly error: TaskFailure }): ReactElement {
  const t = useTranslate()
  return (
    <div className="notice notice--error" role="alert">
      <p className="notice__title">{t(taskErrorMessageKey(error))}</p>
      <code className="notice__detail">{error.message}</code>
    </div>
  )
}

function TaskRow({
  task,
  busy,
  onChanged,
  onFailure,
}: {
  readonly task: Task
  readonly busy: boolean
  readonly onChanged: () => Promise<void>
  readonly onFailure: (error: TaskFailure) => void
}): ReactElement {
  const t = useTranslate()
  const formatters = useFormatters()
  const [working, setWorking] = useState(false)

  const overdue = task.status === 'open' && task.dueOn < today()

  async function run(work: () => Promise<{ ok: boolean; error?: TaskFailure }>): Promise<void> {
    setWorking(true)
    const result = await work()
    if (!result.ok && result.error !== undefined) {
      onFailure(result.error)
    }
    await onChanged()
    setWorking(false)
  }

  return (
    <li className="task" data-status={task.status} data-tone={overdue ? 'overdue' : 'calm'}>
      <div className="task__body">
        <span className="task__due">{formatCalendarDate(formatters, task.dueOn)}</span>
        {/* De titel is door de verhuurder ingevuld en dus data; de soort komt uit
            de catalogus. */}
        <span className="task__title">{task.title}</span>
        <span className="task__kind">{t(`dashboard.tasks.kind.${task.kind}`)}</span>
        {task.assignedToName !== '' && (
          <span className="task__assignee">{task.assignedToName}</span>
        )}
      </div>

      {task.notes !== '' && <p className="task__notes">{task.notes}</p>}

      <div className="task__actions">
        {task.status === 'open' ? (
          <button
            className="button"
            disabled={busy || working}
            onClick={() => void run(async () => updateTask(task.id, { status: 'done' }))}
            type="button"
          >
            {t('dashboard.tasks.action.done')}
          </button>
        ) : (
          <button
            className="button button--quiet"
            disabled={busy || working}
            onClick={() => void run(async () => updateTask(task.id, { status: 'open' }))}
            type="button"
          >
            {t('dashboard.tasks.action.reopen')}
          </button>
        )}
        <button
          className="button button--quiet"
          disabled={busy || working}
          onClick={() => void run(async () => deleteTask(task.id))}
          type="button"
        >
          {t('dashboard.tasks.action.remove')}
        </button>
      </div>
    </li>
  )
}

function NewTaskForm({
  residenceId,
  busy,
  onCreated,
  onFailure,
}: {
  readonly residenceId: string
  readonly busy: boolean
  readonly onCreated: () => Promise<void>
  readonly onFailure: (error: TaskFailure) => void
}): ReactElement {
  const t = useTranslate()
  const [kind, setKind] = useState<TaskKind>('cleaning')
  const [title, setTitle] = useState('')
  const [notes, setNotes] = useState('')
  const [dueOn, setDueOn] = useState(today())
  const [working, setWorking] = useState(false)

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (title.trim() === '') {
      return
    }

    setWorking(true)
    const result = await createTask({
      residenceId,
      kind,
      title: title.trim(),
      notes: notes.trim(),
      dueOn,
    })
    if (result.ok) {
      setTitle('')
      setNotes('')
    } else {
      onFailure(result.error)
    }
    await onCreated()
    setWorking(false)
  }

  return (
    <form
      className="request-panel"
      onSubmit={(event) => {
        void handleSubmit(event)
      }}
    >
      <h3 className="request-panel__title">{t('dashboard.tasks.add.title')}</h3>

      <div className="field-grid">
        <label className="field">
          <span className="field__label">{t('dashboard.tasks.field.title')}</span>
          <input
            className="field__input"
            disabled={busy || working}
            maxLength={200}
            onChange={(event) => {
              setTitle(event.target.value)
            }}
            type="text"
            value={title}
          />
        </label>

        <label className="field">
          <span className="field__label">{t('dashboard.tasks.field.kind')}</span>
          <select
            className="field__input"
            disabled={busy || working}
            onChange={(event) => {
              setKind(event.target.value as TaskKind)
            }}
            value={kind}
          >
            {TASK_KINDS.map((option) => (
              <option key={option} value={option}>
                {t(`dashboard.tasks.kind.${option}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">{t('dashboard.tasks.field.dueOn')}</span>
          <input
            className="field__input"
            disabled={busy || working}
            onChange={(event) => {
              setDueOn(event.target.value)
            }}
            type="date"
            value={dueOn}
          />
        </label>
      </div>

      <label className="field">
        <span className="field__label">{t('dashboard.tasks.field.notes')}</span>
        <textarea
          className="field__input field__input--tall"
          disabled={busy || working}
          maxLength={4000}
          onChange={(event) => {
            setNotes(event.target.value)
          }}
          rows={3}
          value={notes}
        />
      </label>

      <div className="request-panel__actions">
        <button className="button" disabled={busy || working || title.trim() === ''} type="submit">
          {working ? t('dashboard.tasks.add.submitting') : t('dashboard.tasks.add.submit')}
        </button>
      </div>
    </form>
  )
}

export function TasksPage(): ReactElement {
  const t = useTranslate()

  const [residences, setResidences] = useState<readonly ResidenceOption[]>([])
  const [residenceId, setResidenceId] = useState<string | null>(null)
  const [tasks, setTasks] = useState<readonly Task[]>([])
  const [loading, setLoading] = useState(true)
  const [failure, setFailure] = useState<TaskFailure | null>(null)
  const [busy, setBusy] = useState(false)

  const controller = useRef<AbortController | null>(null)

  const loadResidences = useCallback(async (): Promise<void> => {
    const result = await listResidences()
    if (!result.ok) {
      setFailure(result.error)
      setLoading(false)
      return
    }
    setResidences(result.data)
    setResidenceId((current) => current ?? result.data[0]?.id ?? null)
    if (result.data.length === 0) {
      setLoading(false)
    }
  }, [])

  const loadTasks = useCallback(async (): Promise<void> => {
    if (residenceId === null) {
      return
    }
    controller.current?.abort()
    const next = new AbortController()
    controller.current = next

    const result = await listTasks({ residenceId }, { signal: next.signal })
    if (next.signal.aborted) {
      return
    }
    if (!result.ok) {
      if (result.error.kind !== 'aborted') {
        setFailure(result.error)
      }
      setLoading(false)
      return
    }
    setTasks(result.data)
    setLoading(false)
  }, [residenceId])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadResidences()
  }, [loadResidences])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadTasks()
    return () => {
      controller.current?.abort()
    }
  }, [loadTasks])

  const refresh = useCallback(async (): Promise<void> => {
    setBusy(true)
    await loadTasks()
    setBusy(false)
  }, [loadTasks])

  const now = today()
  const overdue = tasks.filter((task) => task.status === 'open' && task.dueOn < now)
  const open = tasks.filter((task) => task.status === 'open' && task.dueOn >= now)
  const closed = tasks.filter((task) => task.status !== 'open')

  return (
    <section className="page">
      <h1 className="page__title">{t('dashboard.tasks.title')}</h1>
      <p className="prose">{t('dashboard.tasks.intro')}</p>

      {failure !== null && <FailureNotice error={failure} />}

      {loading && (
        <p className="page__status" role="status">
          {t('dashboard.tasks.loading')}
        </p>
      )}

      {!loading && residences.length === 0 && (
        <p className="page__status" role="status">
          {t('dashboard.tasks.noResidence')}
        </p>
      )}

      {residences.length > 1 && (
        <label className="field field--inline">
          <span className="field__label">{t('dashboard.tasks.residence')}</span>
          <select
            className="field__input"
            disabled={busy}
            onChange={(event) => {
              setResidenceId(event.target.value)
            }}
            value={residenceId ?? ''}
          >
            {residences.map((residence) => (
              // De naam komt uit de database en wordt niet vertaald.
              <option key={residence.id} value={residence.id}>
                {residence.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {residenceId !== null && (
        <>
          {/* Achterstallig apart en bovenaan: dat is een ander bericht dan
              "volgende week". */}
          {overdue.length > 0 && (
            <section className="page__section">
              <h2 className="page__sectionTitle">
                {t('dashboard.tasks.overdue.title', { count: overdue.length })}
              </h2>
              <ul className="task-list">
                {overdue.map((task) => (
                  <TaskRow
                    busy={busy}
                    key={task.id}
                    onChanged={refresh}
                    onFailure={setFailure}
                    task={task}
                  />
                ))}
              </ul>
            </section>
          )}

          <section className="page__section">
            <h2 className="page__sectionTitle">{t('dashboard.tasks.open.title')}</h2>
            {open.length === 0 ? (
              <p className="page__status">{t('dashboard.tasks.open.empty')}</p>
            ) : (
              <ul className="task-list">
                {open.map((task) => (
                  <TaskRow
                    busy={busy}
                    key={task.id}
                    onChanged={refresh}
                    onFailure={setFailure}
                    task={task}
                  />
                ))}
              </ul>
            )}
          </section>

          {closed.length > 0 && (
            <section className="page__section">
              <h2 className="page__sectionTitle">{t('dashboard.tasks.done.title')}</h2>
              <ul className="task-list">
                {closed.map((task) => (
                  <TaskRow
                    busy={busy}
                    key={task.id}
                    onChanged={refresh}
                    onFailure={setFailure}
                    task={task}
                  />
                ))}
              </ul>
            </section>
          )}

          <NewTaskForm
            busy={busy}
            onCreated={refresh}
            onFailure={setFailure}
            residenceId={residenceId}
          />
        </>
      )}
    </section>
  )
}
